/*
 * Public independent driver application. Mirrors PartnerApply.jsx but for
 * individuals (owner_type='astat', tp_id=null). On submit → pending_review
 * status → admin approves in Credentialing Submissions workspace → auto-push
 * to MedexOva when the required-field set is complete.
 */
import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { isValidE164, autoPrefixUS } from "../../components/PhoneInput";
import { ChevronDown, ChevronRight, ShieldCheck } from "lucide-react";
import { ZonePicker, zipCodesForZoneIds } from "../../components/ZonePicker";
import { useZones } from "../../hooks/useZones";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tpt`;
const INIT = {
  name: "", phone: "", email: "", dob: "", ssn_last4: "", address: "", ein: "",
  dl_number: "", dl_state: "", dl_expiry: "", cdl: false, chauffeur_endorsement: false,
  medical_cert_expiry: "", drug_screen_date: "", background_check_date: "",
  mvr_pull_date: "", mvr_consent_date: "",
  emergency_contact_name: "", emergency_contact_phone: "", emergency_contact_relationship: "",
  vehicle_type: "sedan", assigned_vehicle_plate: "", vehicle_make: "", vehicle_model: "", vehicle_year: "", vehicle_vin: "", notes: "",
  // Service area — used by dispatch to match trips to nearby drivers.
  service_area_type: "radius",
  service_radius_miles: 25,
  service_home_address: "",
  service_zone_ids: [],   // picked from the named zones for the region
  service_zip_codes: "",  // manual fallback, comma-separated, merged with zone ZIPs on submit
  tos_accepted: false, background_check_consent: false, sms_consent: false,
};

function Section({ title, defaultOpen = false, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: "1px solid #E9EAE4", borderRadius: 12, marginBottom: 12, background: "#fff" }}>
      <button type="button" onClick={() => setOpen(!open)}
        style={{ width: "100%", background: "none", border: "none", padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: "#2B4C3B", fontWeight: 700 }}>{title}</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {badge && <span style={{ fontSize: 10, background: "#F1EFE8", color: "#2B4C3B", padding: "3px 8px", borderRadius: 999, fontWeight: 700 }}>{badge}</span>}
          {open ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
        </div>
      </button>
      {open && <div style={{ padding: "0 18px 18px" }}>{children}</div>}
    </div>
  );
}

export default function DriverApply() {
  const [form, setForm] = useState(INIT);
  const { zones } = useZones();
  const [docs, setDocs] = useState({});
  const [uploading, setUploading] = useState(null);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const uploadDoc = async (slot, file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("Max 15 MB"); return; }
    setUploading(slot);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/coi-upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs(d => ({ ...d, [slot]: { doc_id: data.doc_id, filename: data.filename } }));
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "upload failed"); }
    finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    const phone = autoPrefixUS(form.phone);
    const emergency_contact_phone = autoPrefixUS(form.emergency_contact_phone);
    if (!isValidE164(phone)) { toast.error("Please enter a 10-digit US mobile phone number."); return; }
    if (emergency_contact_phone && !isValidE164(emergency_contact_phone)) { toast.error("Please enter a 10-digit US phone number for the emergency contact."); return; }
    if (!form.tos_accepted || !form.background_check_consent) { toast.error("Please accept the Terms of Service and background-check consent to continue."); return; }
    if (!form.sms_consent) { toast.error("Please check the SMS/voice consent box to continue."); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        phone,
        emergency_contact_phone,
        photo_doc_id:            docs.photo_doc_id?.doc_id,
        dl_front_doc_id:         docs.dl_front_doc_id?.doc_id,
        dl_back_doc_id:          docs.dl_back_doc_id?.doc_id,
        medical_cert_doc_id:     docs.medical_cert_doc_id?.doc_id,
        drug_screen_doc_id:      docs.drug_screen_doc_id?.doc_id,
        background_check_doc_id: docs.background_check_doc_id?.doc_id,
      };
      // ZIP codes = union of picked zones' ZIPs + any manually-entered ones
      const manualZips = typeof payload.service_zip_codes === "string"
        ? payload.service_zip_codes.split(/[,\s]+/).map((z) => z.trim()).filter((z) => /^\d{5}$/.test(z))
        : [];
      const zoneZips = zipCodesForZoneIds(zones, payload.service_zone_ids || []);
      payload.service_zip_codes = Array.from(new Set([...zoneZips, ...manualZips]));
      if (!payload.service_zip_codes.length) delete payload.service_zip_codes;
      delete payload.service_zone_ids;
      // Coerce radius
      if (payload.service_radius_miles !== "" && payload.service_radius_miles != null) {
        payload.service_radius_miles = Number(payload.service_radius_miles) || 25;
      }
      // Default home address to residential address if left blank
      if (!payload.service_home_address && payload.address) {
        payload.service_home_address = payload.address;
      }
      Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] == null) && delete payload[k]);
      await axios.post(`${API}/drivers/apply`, payload);
      setSubmitted(true);
    } catch (e2) { toast.error(e2?.response?.data?.detail || "Submission failed"); }
    finally { setBusy(false); }
  };

  const F = (label, k, type = "text", extra = {}) => (
    <label style={{ display: "block" }} key={k}>
      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>{label}</div>
      <input value={form[k]} onChange={(e) => set(k, e.target.value)} type={type} data-testid={`drv-apply-${k}`}
        onBlur={type === "tel" ? () => set(k, autoPrefixUS(form[k])) : undefined}
        style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }} {...extra}/>
    </label>
  );

  const Doc = ({ slot, label }) => (
    <div style={{ marginBottom: 8, background: "#F9F8F6", border: "1px dashed #D0CDC0", borderRadius: 10, padding: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#0F172A" }}>{label}</div>
      <input type="file" accept=".pdf,.png,.jpg,.jpeg" disabled={uploading === slot}
        onChange={(e) => uploadDoc(slot, e.target.files?.[0])}
        data-testid={`drv-apply-doc-${slot}`}
        style={{ display: "block", width: "100%", fontSize: 11, marginTop: 6 }}/>
      {docs[slot] && <div style={{ fontSize: 10, color: "#2B4C3B", fontWeight: 600, marginTop: 3 }}>✓ {docs[slot].filename}</div>}
    </div>
  );

  if (submitted) return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6", display: "grid", placeItems: "center", padding: 24 }}>
      <div style={{ maxWidth: 480, textAlign: "center", background: "#fff", border: "1px solid #E9EAE4", borderRadius: 14, padding: 40 }} data-testid="drv-apply-submitted">
        <div style={{ width: 60, height: 60, borderRadius: "50%", background: "#16A34A", color: "#fff", display: "grid", placeItems: "center", fontSize: 28, fontWeight: 800, margin: "0 auto 16px" }}>✓</div>
        <div style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", marginBottom: 8 }}>Application received</div>
        <div style={{ color: "#64748B", fontSize: 14 }}>Thanks for applying. Our credentialing team will review and reach out within 2 business days.</div>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: "100vh", background: "#F9F8F6", padding: "40px 20px" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: "linear-gradient(135deg,#2B4C3B,#3A7BD5)", display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: 16 }}>A</div>
          <div>
            <div style={{ fontWeight: 800, color: "#0F172A", fontSize: 18 }}>Drive with MedexOVA</div>
            <div style={{ fontSize: 13, color: "#64748B" }}>Independent driver application. Complete credentialing → get dispatched. Paid weekly, direct deposit.</div>
          </div>
        </div>
        <div style={{ background: "#EEF6EE", border: "1px solid #C6E6C6", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 10 }}>
          <ShieldCheck size={18} color="#2B4C3B" style={{ flexShrink: 0, marginTop: 2 }}/>
          <div style={{ fontSize: 12, color: "#0F172A" }}>
            <b>Only your name, phone, email and home address are required to apply.</b> Everything else is optional for now — our team can help you complete it after you apply.
          </div>
        </div>
        <form onSubmit={submit}>
          <Section title="Identity" defaultOpen badge="Required">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Full legal name *", "name", "text", { required: true })}
              {F("Mobile phone *", "phone", "tel", { required: true, placeholder: "(940) 555-0100" })}
              {F("Email *", "email", "email", { required: true })}
              {F("Date of birth", "dob", "date")}
              {F("SSN last 4", "ssn_last4", "text", { maxLength: 4 })}
              {F("Home address *", "address", "text", { required: true })}
              {F("EIN (if you operate as your own LLC/business)", "ein", "text", { placeholder: "XX-XXXXXXX" })}
            </div>
          </Section>
          <Section title="Driver's license" badge="Optional">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {F("DL #", "dl_number")}
              {F("State", "dl_state", "text", { maxLength: 2 })}
              {F("Expiry", "dl_expiry", "date")}
            </div>
            <div style={{ display: "flex", gap: 20, marginTop: 12, fontSize: 13 }}>
              <label><input type="checkbox" checked={form.cdl} onChange={(e) => set("cdl", e.target.checked)}/> Holds CDL</label>
              <label><input type="checkbox" checked={form.chauffeur_endorsement} onChange={(e) => set("chauffeur_endorsement", e.target.checked)}/> Chauffeur endorsement</label>
            </div>
          </Section>
          <Section title="Medical & background" badge="Optional">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              {F("Medical cert expiry", "medical_cert_expiry", "date")}
              {F("Last drug-screen", "drug_screen_date", "date")}
              {F("Background check date", "background_check_date", "date")}
              {F("MVR pull date", "mvr_pull_date", "date")}
              {F("MVR consent signed", "mvr_consent_date", "date")}
            </div>
          </Section>
          <Section title="Emergency contact" badge="Optional">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              {F("Name", "emergency_contact_name")}
              {F("Phone", "emergency_contact_phone", "tel", { placeholder: "(940) 555-0100" })}
              {F("Relationship", "emergency_contact_relationship")}
            </div>
          </Section>
          <Section title="Documents" badge="Optional">
            <Doc slot="photo_doc_id" label="Headshot"/>
            <Doc slot="dl_front_doc_id" label="DL front"/>
            <Doc slot="dl_back_doc_id" label="DL back"/>
            <Doc slot="medical_cert_doc_id" label="Medical examiner's cert"/>
            <Doc slot="drug_screen_doc_id" label="Drug screen result"/>
            <Doc slot="background_check_doc_id" label="Background check report"/>
          </Section>
          <Section title="Service area" defaultOpen badge="Optional">
            <div style={{ fontSize: 12, color: "#64748B", marginBottom: 10 }}>
              Tell us where you&apos;ll drive. We&apos;ll match trips to your area before offering them to other providers.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <label style={{ display: "block" }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Service area type</div>
                <select value={form.service_area_type} onChange={(e) => set("service_area_type", e.target.value)}
                  data-testid="drv-apply-service_area_type"
                  style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}>
                  <option value="radius">Radius from home address</option>
                  <option value="zips">Specific ZIP codes</option>
                  <option value="both">Both</option>
                </select>
              </label>
              {(form.service_area_type === "radius" || form.service_area_type === "both") && (
                <label style={{ display: "block" }}>
                  <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Radius (miles)</div>
                  <input type="number" min="1" max="200" value={form.service_radius_miles}
                    onChange={(e) => set("service_radius_miles", e.target.value)}
                    data-testid="drv-apply-service_radius_miles"
                    style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}/>
                </label>
              )}
            </div>
            {(form.service_area_type === "radius" || form.service_area_type === "both") && (
              <label style={{ display: "block", marginTop: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Home address for radius center (leave blank to use home address above)</div>
                <input type="text" value={form.service_home_address}
                  onChange={(e) => set("service_home_address", e.target.value)}
                  data-testid="drv-apply-service_home_address"
                  placeholder="e.g. 123 Main St, Chicago, IL 60620"
                  style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}/>
              </label>
            )}
            {(form.service_area_type === "zips" || form.service_area_type === "both") && (
              <label style={{ display: "block", marginTop: 12 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Zones you serve</div>
                <ZonePicker selectedIds={form.service_zone_ids} onChange={(ids) => set("service_zone_ids", ids)} testId="drv-apply-zone-picker" />
                <details style={{ marginTop: 8 }}>
                  <summary style={{ fontSize: 12, color: "#64748B", cursor: "pointer" }}>Serve a ZIP outside these zones? Add it manually</summary>
                  <input type="text" value={form.service_zip_codes}
                    onChange={(e) => set("service_zip_codes", e.target.value)}
                    data-testid="drv-apply-service_zip_codes"
                    placeholder="60620, 60453, 60466"
                    style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14, marginTop: 6 }}/>
                </details>
              </label>
            )}
          </Section>
          <Section title="Vehicle & notes" badge="Optional">
            <label style={{ display: "block", marginBottom: 12 }}>
              <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.12em", color: "#64748B", marginBottom: 4 }}>Vehicle type</div>
              <select value={form.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)}
                style={{ width: "100%", height: 40, border: "1px solid #E2E0D8", borderRadius: 8, padding: "0 12px", fontSize: 14 }}>
                {["sedan","suv","wheelchair","stretcher"].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              {F("Make", "vehicle_make")}
              {F("Model", "vehicle_model")}
              {F("Year", "vehicle_year", "number")}
              {F("VIN", "vehicle_vin")}
              <div style={{ gridColumn: "1 / -1" }}>{F("License plate", "assigned_vehicle_plate")}</div>
            </div>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} placeholder="Anything else we should know?"
              style={{ width: "100%", border: "1px solid #E2E0D8", borderRadius: 8, padding: 12, fontSize: 14 }}/>
          </Section>
          <Section title="Consent" defaultOpen badge="Required">
            <div style={{ display: "flex", flexDirection: "column", gap: 10, fontSize: 13, color: "#0F172A" }}>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8 }} data-testid="drv-apply-tos-wrap">
                <input type="checkbox" checked={form.tos_accepted} onChange={(e) => set("tos_accepted", e.target.checked)}
                  data-testid="drv-apply-tos" style={{ marginTop: 2 }} />
                <span>I agree to MedexOVA&apos;s <a href="/terms" target="_blank" rel="noreferrer" style={{ color: "#2B4C3B", fontWeight: 700, textDecoration: "underline" }}>Terms of Service</a>.</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8 }} data-testid="drv-apply-bgcheck-wrap">
                <input type="checkbox" checked={form.background_check_consent} onChange={(e) => set("background_check_consent", e.target.checked)}
                  data-testid="drv-apply-bgcheck" style={{ marginTop: 2 }} />
                <span>I consent to a motor vehicle record (MVR) pull, background check, and drug screen as part of credentialing.</span>
              </label>
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, background: "#F9F8F6", padding: 10, borderRadius: 8 }} data-testid="drv-apply-sms-consent-wrap">
                <input type="checkbox" checked={form.sms_consent} onChange={(e) => set("sms_consent", e.target.checked)}
                  data-testid="drv-apply-sms-consent" style={{ marginTop: 2 }} />
                <span>
                  I agree to receive automated SMS and voice messages from <b>MedexOVA</b> about my
                  application, credentialing, and — once active — trip offers and dispatch updates.
                  Message frequency varies by dispatch activity. Msg &amp; data rates may apply. Reply{" "}
                  <b>STOP</b> to cancel, <b>HELP</b> for help. See our{" "}
                  <a href="/sms-terms" target="_blank" rel="noreferrer" style={{ color: "#2B4C3B", fontWeight: 700, textDecoration: "underline" }}>SMS Terms</a>.
                </span>
              </label>
            </div>
          </Section>
          <button disabled={busy} type="submit" data-testid="drv-apply-submit"
            style={{ width: "100%", height: 50, borderRadius: 10, background: "#2B4C3B", color: "#fff", fontWeight: 700, fontSize: 15, border: "none", cursor: "pointer" }}>
            {busy ? "Submitting…" : "Submit application"}
          </button>
        </form>
      </div>
    </div>
  );
}
